import { FeeDto, AllowanceDto, ReturnPropertyDto, ReturnStatus, ReturnType } from './cashering.model';
export { ReturnStatus } from './cashering.model';

// How a return was created (orthogonal to base type; Duplicate lives here).
export type ReturnOrigin =
  'AUTO_GENERATED' | 'STAFF_MANUAL' | 'DUPLICATE' | 'TAX_OFFICE_ASSESSMENT' | 'SELF_SERVE' | 'PLAN_MATERIALIZED' | 'SUPPLEMENTAL';

// ===== Return Summary DTO (for lists/grids) =====

export interface ReturnSummaryDto {
  id: number;
  accountId: number;
  accountName: string;
  customerId: string;
  period: string;
  type: ReturnType;
  origin?: ReturnOrigin;
  delinquencyCaseId?: number | null;
  assessmentCaseId?: number | null;
  installmentPlanId?: number | null;
  lienCaseId?: number | null;
  /** Underlying return consumed by a plan/assessment/lien: unpayable, read-only. */
  rolledUp?: boolean;
  frequency: string;
  dueDate: string;
  status: ReturnStatus;
  propertyCount?: number;
  totalTaxDue?: number;
  totalFees?: number;
  totalAllowances?: number;
  totalAmountDue: number;
  balanceDue: number;
  amountPaid: number;
  createdAt: string;
  properties?: any[];
  // Shared property returns: shared = the viewing account is a guest of the owning account.
  owningAccountId?: number | null;
  owningAccountName?: string | null;
  shared?: boolean | null;
  reportedByRole?: string | null;
}

// ===== Advanced-query search criteria (POST /api/returns/search) =====

export interface ReturnSearchCriteria {
  accountId?: number | null;
  customerId?: string | null;
  statuses?: ReturnStatus[] | null;
  frequency?: string | null;
  period?: string | null;        // YYYY-MM
  dueDateFrom?: string | null;   // ISO date
  dueDateTo?: string | null;     // ISO date
  hasBalanceDue?: boolean | null;
}

// ===== Create Return request (admin "new return" flow) =====

export interface CreateReturnRequest {
  accountId: number;
  period?: string;          // YYYY-MM (omit when duplicating)
  frequency?: string;       // MONTHLY | QUARTERLY | ANNUAL | SEASONAL
  dueDate?: string;         // optional ISO date; backend computes when omitted
  duplicateFromReturnId?: number;
}

// ===== Assessment (Phase 2) =====

export type AssessmentCause =
  'UNDERREPORTED' | 'INCORRECT_DEDUCTIONS' | 'OFFICE_ADJUSTMENT' | 'AUDIT_FINDING' | 'DELINQUENT_ROLLUP';

export interface CreateAssessmentRequest {
  accountId: number;
  cause: AssessmentCause;
  auditReference?: string;
  statuteBasis?: string;
  overrideSol: boolean;
  justification?: string;
  periods: { period: string; additionalTax: number; penalty?: number; interest?: number }[];
}

// ===== Lien (Phase 4) =====

export interface CreateLienRequest {
  accountId: number;
  returnIds: number[];
  noticeSentDate: string;   // ISO date
  demandIssuedDate: string;
  cureDeadline: string;
  statuteBasis?: string;
  lienRecordedReference?: string;
  /** Overrides the catalog clerk-of-court filing fee (auto-applied when omitted). */
  lienFeeAmount?: number;
  /** Single due date of the materialized LIEN return (backend defaults to +30 days). */
  dueDate?: string;
}

export interface LienCaseDto {
  id: number;
  accountId: number;
  status: string;
  returnIds: number[];
}

// ===== Installment (Phase 3) =====

export interface CreateStaffPlanRequest {
  accountId: number;
  returnIds: number[];
  downPaymentAmount: number;
  installmentCount: number;
  /** First payment due date (ISO); backend defaults to the 1st of next month. */
  firstDueDate?: string;
  overrides?: FeeOverride[];
}

/** Staff override of a return's computed penalty/interest ("honest mistake" relief). */
export interface FeeOverride {
  returnId: number;
  penalty?: number | null;
  interest?: number | null;
}

/** Dry-run of a plan/assessment/lien: per-return breakdown + totals (+ schedule for plans). */
export interface PlanPreview {
  totalTax: number;
  totalPenalty: number;
  totalInterest: number;
  total: number;
  returns: {
    returnId: number; period: string; tax: number; penalty: number; interest: number; balance: number;
  }[];
  schedule: { sequenceNo: number; period: string; dueDate: string; amount: number }[];
  pendingRateReturnIds: number[];
}

export interface PlanDetail {
  planId: number;
  status: string;
  totalAmount: number;
  totalPaid: number;
  totalLeft: number;
  installmentCount: number;
  firstDueDate?: string;
  schedule: {
    installmentId: number; sequenceNo: number; period: string; dueDate: string;
    amount: number; status: string; taxReturnId?: number;
  }[];
  underlyingReturns: {
    returnId: number; period: string; type: string; dueDate: string; status: string;
    totalAmountDue: number; amountPaid: number; balanceDue: number;
  }[];
}

/** Roll-up assessment: existing returns -> one lump-sum ASSESSMENT return. */
export interface RollupAssessmentRequest {
  accountId: number;
  returnIds: number[];
  dueDate: string;          // ISO
  cause: AssessmentCause;
  auditReference?: string;
  statuteBasis?: string;
  calculationDate?: string; // ISO
  overrides?: FeeOverride[];
}

export interface StaffPlanResult {
  planId: number;
  status: string;
  totalAmount: number;
  downPaymentAmount: number;
  installmentCount: number;
  returnIds: number[];
}

export interface AssessmentCaseDto {
  id: number;
  accountId: number;
  cause: AssessmentCause;
  status: string;
  auditReference?: string;
  statuteBasis?: string;
  createdAt: string;
  createdBy: string;
  returnIds: number[];
}

// ===== Return Detail DTO (for detail view/editing) =====

export interface ReturnDetailDto {
  id: number;
  accountId: number;
  accountName: string;
  customerId: string;
  period: string;
  type: ReturnType;
  frequency: string;
  dueDate: string;
  status: ReturnStatus;
  calculationDate?: string;
  customerEditingEnabled: boolean;

  // Property details
  properties: ReturnPropertyDto[];

  // Fees and allowances
  fees: FeeDto[];
  allowances: AllowanceDto[];

  // Calculated totals
  grossRentalReceipts: number;
  totalExemptions: number;
  taxableRentalReceipts: number;
  taxRate: number;
  totalTaxes: number;
  totalFees: number;
  totalAllowances: number;
  totalAmountDue: number;
  amountPaid: number;
  balanceDue: number;

  // Metadata
  createdAt: string;
  updatedAt: string;
  createdBy: string;
  updatedBy: string;

  /** Optimistic lock: send back as ReturnUpdateDto.expectedVersion. */
  version: number;
  owningAccountId?: number | null;
  owningAccountName?: string | null;
  reportedByRole?: string | null;
  reportedByAccountId?: number | null;
}

// ===== Return Update DTO (for editing) =====

export interface ReturnUpdateDto {
  id: number;
  calculationDate?: string;
  customerEditingEnabled?: boolean;
  properties: ReturnPropertyUpdateDto[];
  /** Required by the backend: the loaded ReturnDetailDto.version; a mismatch is a 409. */
  expectedVersion: number;
}

export interface ReturnPropertyUpdateDto {
  propertyId: number;
  grossRentalReceipts: number;
  exemptions: number;
  exemptionTypeId?: number | null;
  exemptionExplanation?: string | null;
}

// ===== Legacy models for backward compatibility =====

export interface ReturnModel {
  id: number;
  accountId: number;
  period: string;
  type: string;
  frequency: string;
  dueDate: string;
  status: ReturnStatus;
  calculationDate?: string;
  properties: ReturnPropertyModel[];
  grossRentalReceipts: number;
  exemptions: number;
  taxableRentalReceipts: number;
  totalTaxes: number;
  lateFee: number;
  lateInterest: number;
  nsfFee: number;
  attorneyFee: number;
  lienFee: number;
  onlineCollectionAllowance: number;
  credit: number;
  totalAmountDue: number;
  customFees: CustomFeeAllowanceModel[];
  customAllowances: CustomFeeAllowanceModel[];
  notes: ReturnNoteModel[];
  attachments: ReturnAttachmentModel[];
  history: ReturnHistoryEntryModel[];
  enableCustomerEditing: boolean;
  version?: number;
  owningAccountId?: number | null;
  owningAccountName?: string | null;
  reportedByRole?: string | null;
  reportedByAccountId?: number | null;
}

export interface ReturnPropertyModel {
  id: number;
  address: string;
  grossRentalReceipts: number;
  exemptions: number;
  exemptionTypeId?: number | null;
  exemptionExplanation?: string | null;
}

export interface CustomFeeAllowanceModel {
  id?: number;
  name: string;
  amount: number;
  isNew?: boolean;
}

export interface ReturnNoteModel {
  id: number;
  content: string;
  createdDate: string;
  createdBy: string;
}

export interface ReturnAttachmentModel {
  id: number;
  fileName: string;
  fileSize: number;
  uploadedDate: string;
  uploadedBy: string;
  downloadUrl: string;
}

export interface ReturnHistoryEntryModel {
  id: number;
  date: string;
  type: HistoryType;
  user: string;
  attribute: string;
  oldValue: string;
  newValue: string;
}

export enum HistoryType {
  EDIT = 'Edit',
  NOTE = 'Note',
  ATTACHMENT = 'Attachment',
  STATUS_CHANGE = 'Status Change'
}
